import type { CSSProperties, RefObject } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

export interface CollapsibleHeightResult {
    contentRef: RefObject<HTMLDivElement>;
    style: CSSProperties;
}

export function useCollapsibleHeight(
    collapsed: boolean,
): CollapsibleHeightResult {
    const contentRef = useRef<HTMLDivElement>(null);
    const [contentHeight, setContentHeight] = useState(0);

    useLayoutEffect(() => {
        const element = contentRef.current;

        if (!element) {
            return;
        }

        const updateContentHeight = (): void => {
            const nextHeight = element.scrollHeight;

            setContentHeight((height) =>
                height === nextHeight ? height : nextHeight,
            );
        };

        updateContentHeight();

        const resizeObserver = new ResizeObserver(updateContentHeight);

        resizeObserver.observe(element);

        return () => resizeObserver.disconnect();
    }, [collapsed]);

    return {
        contentRef,
        style: {
            '--collapsible-content-height': `${contentHeight}px`,
        } as CSSProperties,
    };
}
